import {Box, Grid, Menu, MenuItem, MenuList, VStack} from '@chakra-ui/react';
import {ButtonUser} from './ButtonUser';
import {EgalimIcon} from '@/components/svg/EgalimIcon';
import {UserBlackIcon} from '@/components/svg/UserBlackIcon';
import {OrderIcon} from '@/components/svg/OrderIcon';
import {FactureIcon} from '@/components/svg/FactureIcon';
import {ShippingIconBlack} from '@/components/svg/ShippingBlackIcon';
import {useClient, useMe} from '@/haligator-factory';
import {HaliRenderOne, useHalState} from '@synako/haligator';
import {routes} from 'ff-common';
import {Text} from '@chakra-ui/react';
import {SettingsIcon} from '@/components/svg/SettingsIcon';
import {RepeatIcon, UnlockIcon} from '@chakra-ui/icons';
import {useTranslation} from 'react-i18next';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {Link} from 'react-router-dom';
import {ContactsIcon} from '@/components/svg/ContactsIcon';

export const UserMenu = () => {
  const {t} = useTranslation();
  const client = useClient();
  const meNav = useMe();
  const meState = useHalState(meNav);
  const switch_ = useHalState(meNav.follow('switch'));
  const {buildClientRoute} = useClientRoute();

  const contact = meNav.follow('contact');

  const listMenuItems: {
    icon: React.ReactNode;
    label: string;
    color?: string;
    route?: typeof routes.client.profil;
    href?: string;
  }[] = [
    {
      icon: <UserBlackIcon />,
      label: t('menu.informations'),
      route: routes.client.profil,
    },
    {
      icon: <OrderIcon />,
      label: t('menu.commandes'),
      route: routes.client.commandes,
    },
    {
      icon: <ShippingIconBlack />,
      label: t('menu.bons_de_livraison'),
      route: routes.client.livraisons,
    },
    {
      icon: (
        <FactureIcon
          color='black'
          height='25px'
          width='25px'
        />
      ),
      label: t('menu.factures_et_avoirs'),
      route: routes.client.factures,
    },
    {
      icon: (
        <ContactsIcon
          color='black'
          height='23px'
          width='23px'
        />
      ),
      label: t('menu.contacts'),
      route: routes.client.contacts,
    },
  ];

  return (
    <HaliRenderOne
      nav={client}
      render={({data}) => (
        <Box
          display='flex'
          alignItems='center'
        >
          <Menu>
            <ButtonUser
              nom={data.tolede.raison_sociale ?? data.tolede.code_client}
            />
            <MenuList position='relative'>
              {listMenuItems.map((item) => (
                <UserMenuItem
                  key={`usermenu_${item.label}`}
                  icon={item.icon}
                  label={item.label}
                  color={item.color}
                  href={
                    item.href ??
                    (item.route ? buildClientRoute(item.route) : undefined)
                  }
                />
              ))}
              {/* Préférences : affiché uniquement si le contact a la commande c:changer-preferences */}
              <HaliRenderOne
                nav={contact}
                render={({state: contactState}) =>
                  contactState.hasCommand('c:changer-preferences') ? (
                    <UserMenuItem
                      icon={<SettingsIcon color='black' />}
                      label={t('menu.preferences')}
                      href={buildClientRoute(routes.client.preferences)}
                    />
                  ) : null
                }
              />
              {/* ÉGalim : affiché si le lien synthese-egalim est disponible */}
              {meState.state?.hasOneLink('synthese-egalim') && (
                <UserMenuItem
                  icon={
                    <EgalimIcon
                      color='green.egalim'
                      width='23px'
                      height='23px'
                    />
                  }
                  label={t('menu.egalim')}
                  color='green.egalim'
                  href={buildClientRoute(routes.client.egalim)}
                />
              )}
              {/* Changer de client : affiché si plusieurs contacts */}
              {(switch_.data?.items.length ?? 0) > 1 && (
                <UserMenuItem
                  icon={<RepeatIcon color='black' />}
                  label={t('menu.changer_de_client')}
                  href={routes.client.clientSelect.path}
                />
              )}
              <UserMenuItem
                icon={<UnlockIcon color='black' />}
                label={t('menu.deconnecter')}
                href='/authn/logout'
              />
              <Box borderTop='1px solid gray.light' />
              <MenuItem
                as={VStack}
                color='gray.dark'
                fontWeight='600'
                fontSize='sm'
              ></MenuItem>
            </MenuList>
          </Menu>
        </Box>
      )}
    />
  );
};

export const UserMenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  color?: string;
  href: string | undefined;
  onClick?: () => void;
}> = ({icon, label, color, href, onClick}) => {
  // condition pour utiliser soit un lien <a> pour la déconnexion, soit un Link  pour les routes
  if (href === '/authn/logout') {
    return (
      <a href={href}>
        <MenuItem
          as={Grid}
          fontSize='sm'
          fontWeight='500'
        >
          <VStack
            mr='10px'
            width='26px'
            justifyContent='center'
          >
            {icon}
          </VStack>
          <Text color={color}>{label}</Text>
        </MenuItem>
      </a>
    );
  }

  return (
    <Link
      to={href ?? '#'}
      onClick={onClick}
    >
      <MenuItem
        as={Grid}
        fontSize='sm'
        fontWeight='500'
      >
        <VStack
          mr='10px'
          width='26px'
          justifyContent='center'
        >
          {icon}
        </VStack>
        <Text color={color}>{label}</Text>
      </MenuItem>
    </Link>
  );
};
